import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from "react-icons/fi";
// [obszar 3, 2026-09-17] Maska DD.MM.RRRR i granice lat 1910–2099 — jedno źródło dla obu pickerów.
import {
    ROK_DOLNY,
    ROK_GORNY,
    KOMUNIKAT_ROK,
    KOMUNIKAT_ZAKRES,
    ograniczRokWidoku,
    przeliczMaske,
    kasujWTyl,
    rokWZakresie,
    rokZISO,
} from "./GraniceDaty";
import styles from "./DatePicker.module.css";

/**
 * [R23, polecenie właściciela 2026-09-13] NAGŁÓWEK KALENDARZA: LISTA MIESIĘCY, LISTA LAT, SKOK ±10 LAT
 * I DATA WPISANA Z KLAWIATURY.
 *
 * Właściciel: „w sekcji eteczka dodaj opcję datapicker wyjątkowo z opcją zmiany lat i miesięcy, teraz jak
 * wstawiam datę urodzenia muszę cofać się i klikać nawet 300x - to duży błąd."
 *
 * BYŁO ŹLE: jedyną nawigacją kalendarza były strzałki „poprzedni / następny miesiąc”. Data urodzenia z roku 1985
 * to od dziś ~500 kliknięć — kadrowa wypełniająca e-teczkę cofała się miesiąc po miesiącu.
 *
 * NAPRAWA siedzi w JEDNYM miejscu: DatePicker i DateTimePicker składają nagłówek z tego pliku, więc każde pole
 * daty w aplikacji (e-teczka, wnioski HR, grafik, projekty…) dostaje naraz:
 *   - listę miesięcy i listę lat (1930 … bieżący rok + 10; rok właśnie oglądany zawsze jest na liście),
 *   - skoki o 10 lat w obie strony,
 *   - pole „Wpisz datę” (dd.mm.rrrr) — pełna, poprawna data ustawia kalendarz I wartość bez ani jednego kliknięcia;
 *     Enter zamyka kalendarz. Wartość pola dalej idzie do rodzica w tym samym formacie co dotąd (RRRR-MM-DD),
 *     więc żaden zapis na serwer się nie zmienia.
 *
 * [obszar 3, zgłoszenie właściciela 2026-09-17] MASKA I GRANICE LAT — dołożone do tego samego nagłówka:
 *   - pole wpisu KROPKUJE SAMO (DD.MM.RRRR): człowiek pisze cyfry, kropki wstawia maska, a przy edycji
 *     w środku kursor zostaje przy tej samej cyfrze (patrz GraniceDaty.przeliczMaske),
 *   - wklejenie przechodzi przez tę samą maskę; wklejona pełna data w innym zapisie („1.3.1985",
 *     „1985-03-12", „12/03/1985") ląduje w polu od razu jako dd.mm.rrrr,
 *   - rok poza 1910–2099 nie przechodzi ŻADNĄ drogą: ani wpisem, ani wklejeniem, ani z listy lat
 *     (jest przycięta do granic), ani skokiem o miesiąc / o 10 lat (przycisk przy krawędzi jest
 *     wyłączony, zamiast wywozić widok w rok 1830 albo 3423),
 *   - wartość Z ZEWNĄTRZ z rokiem spoza granic (dokładnie ta ze zrzutu właściciela: „23 grudnia 3423")
 *     dostaje komunikat od razu po otwarciu kalendarza, a nie po cichu zostaje.
 */

export const MONTHS_PL = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
];

/** Najstarszy rok NA LIŚCIE — daty urodzenia czynnych pracowników; starsze (do 1910) wpisuje się z klawiatury. */
export const ROK_MIN = 1930;
/** Ile lat W PRZÓD od bieżącego roku ma lista (terminy umów, ważność szkoleń). */
export const LAT_W_PRZOD = 10;

/**
 * Lista lat do wyboru: od ROK_MIN do (rok bieżący + LAT_W_PRZOD). Rok właśnie oglądany zawsze jest na liście —
 * inaczej `<select>` z wartością spoza opcji pokazywałby pustkę (np. data sprzed 1930 wpisana z klawiatury).
 *
 * [obszar 3] Lista jest PRZYCIĘTA do 1910–2099. Bez tego rok 3423 ze zrzutu właściciela rozciągał ją do
 * ~1500 pozycji i sam stawał się jedną z nich — czyli lista LEGALIZOWAŁA wartość, której pole nie przyjmuje.
 */
export function zakresLat(rokWidoku, rokDzis = new Date().getFullYear()) {
    const wGranicach = (r) => Math.min(ROK_GORNY, Math.max(ROK_DOLNY, r));
    const od = wGranicach(Math.min(ROK_MIN, rokWidoku));
    const doRoku = wGranicach(Math.max(rokDzis + LAT_W_PRZOD, rokWidoku));
    const lata = [];
    for (let r = od; r <= doRoku; r += 1) lata.push(r);
    return lata;
}

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Pełna data z klawiatury → "RRRR-MM-DD" albo null, gdy wpis jest niepełny lub nie istnieje w kalendarzu.
 * Przyjmuje "12.03.1985", "12-03-1985", "12/03/1985", "1.3.1985", same cyfry "12031985" oraz ISO "1985-03-12".
 * Rok MUSI mieć cztery cyfry — "12.03.85" nie zgaduje stulecia, tylko czeka na dalszy wpis.
 *
 * ⚠ Ta funkcja mówi tylko „czy taka data ISTNIEJE w kalendarzu". Granice 1910–2099 są sprawdzane OSOBNO,
 * bo rok 3423 ma dostać KOMUNIKAT, a nie zniknąć bez śladu jak wpis niepełny.
 */
export function parsujDateZKlawiatury(tekst) {
    if (typeof tekst !== "string") return null;
    const t = tekst.trim();
    let d;
    let m;
    let y;
    let dop = t.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
    if (dop) {
        d = Number(dop[1]); m = Number(dop[2]); y = Number(dop[3]);
    } else if ((dop = t.match(/^(\d{2})(\d{2})(\d{4})$/))) {
        d = Number(dop[1]); m = Number(dop[2]); y = Number(dop[3]);
    } else if ((dop = t.match(/^(\d{4})-(\d{2})-(\d{2})$/))) {
        y = Number(dop[1]); m = Number(dop[2]); d = Number(dop[3]);
    } else {
        return null;
    }
    if (m < 1 || m > 12 || d < 1 || d > 31) return null;
    const data = new Date(y, m - 1, d);
    // 31.02 „przewija się” na marzec — taka data nie istnieje, więc nie jest poprawnym wpisem.
    if (data.getFullYear() !== y || data.getMonth() !== m - 1 || data.getDate() !== d) return null;
    return `${y}-${pad2(m)}-${pad2(d)}`;
}

/** "1985-03-12" (także z częścią czasu) → "12.03.1985"; pusto/nieznane → "". */
export function formatujDdMmRrrr(dateStr) {
    const dop = typeof dateStr === "string" ? dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
    return dop ? `${dop[3]}.${dop[2]}.${dop[1]}` : "";
}

/**
 * @param {Date}     viewDate       oglądany miesiąc (dowolny dzień tego miesiąca)
 * @param {Function} onViewDate     (Date) → ustaw oglądany miesiąc
 * @param {string}   dataStr        wybrana data "RRRR-MM-DD" (może być pusta) — zasila pole wpisu
 * @param {Function} onWpisanaData  ("RRRR-MM-DD") → pełna data z klawiatury: ustaw wartość i kalendarz
 * @param {Function} [onZatwierdz]  Enter przy poprawnej dacie (DatePicker zamyka kalendarz)
 */
export default function NaglowekKalendarza({ viewDate, onViewDate, dataStr, onWpisanaData, onZatwierdz }) {
    const rok = viewDate.getFullYear();
    const miesiac = viewDate.getMonth();
    /* `rew` (rewizja) rośnie przy KAŻDYM przejściu tekstu przez maskę, także gdy maska oddała ten sam
       napis (np. wpisana litera albo druga kropka). Bez tego React nie przerysowałby pola, a kursor
       zostałby tam, gdzie go postawiła przeglądarka — czyli po znaku, którego w polu już nie ma. */
    const [wpisPola, setWpisPola] = useState(() => ({ tekst: formatujDdMmRrrr(dataStr), rew: 0 }));
    const tekst = wpisPola.tekst;
    // Pełna, istniejąca data, której pole NIE przyjęło — mówimy KTÓRA reguła ją odrzuciła.
    const [powod, setPowod] = useState(null);
    const poleRef = useRef(null);
    const kursorDoUstawienia = useRef(null);

    /* Kursor ustawiamy PO przerysowaniu pola (useLayoutEffect, czyli przed malowaniem — bez mrugnięcia).
       Pole jest sterowane: React wpisuje w nie wartość ze stanu, a przeglądarka stawia wtedy kursor na
       końcu. Maska zna właściwą pozycję (tę samą cyfrę co przed maskowaniem) i tu ją przywracamy.

       ⚠ NAJPIERW ZRÓWNUJEMY TREŚĆ POLA ZE STANEM. Gdy maska oddała ten SAM napis, który już siedzi
       w stanie (np. wstawienie cyfry w środku pełnej daty albo dziewiąta cyfra, która nie wchodzi),
       React nie ma czego przerysowywać — ale po zakończeniu obsługi zdarzenia i tak przywraca sterowaną
       wartość do pola, a to przestawia kursor NA KONIEC. Przywrócenie porównuje treść pola z wartością
       ze stanu, więc gdy sami je zrównamy, nie ma już czego przywracać i kursor zostaje na miejscu. */
    useLayoutEffect(() => {
        const poz = kursorDoUstawienia.current;
        if (poz === null) return;
        kursorDoUstawienia.current = null;
        const pole = poleRef.current;
        if (!pole) return;
        if (pole.value !== wpisPola.tekst) pole.value = wpisPola.tekst;
        if (typeof pole.setSelectionRange !== "function") return;
        try {
            pole.setSelectionRange(poz, poz);
        } catch (e) {
            /* Pole bez obsługi zaznaczenia (starsze przeglądarki dla inputMode=numeric) — maska działa dalej. */
        }
    }, [wpisPola]);

    // Wartość zmieniona z zewnątrz (klik w dzień, „Dzisiaj”, „Wyczyść”, nowy prop) odświeża pole wpisu.
    useEffect(() => {
        setWpisPola({ tekst: formatujDdMmRrrr(dataStr), rew: 0 });
        /* Wartość zapisana KIEDYŚ z rokiem poza granicami (na zrzucie właściciela: 3423) tłumaczy się
           sama, zamiast czekać, aż ktoś ją zauważy na karcie. */
        const rokWartosci = rokZISO(dataStr);
        setPowod(rokWartosci !== null && !rokWZakresie(rokWartosci) ? KOMUNIKAT_ROK : null);
    }, [dataStr]);

    /**
     * Cel skoku strzałki, już przycięty do granic; `null` = nie ma gdzie iść (przycisk wyłączony).
     * Skok o 10 lat przy krawędzi DOCIĄGA do roku granicznego (ten sam miesiąc), a strzałka miesiąca
     * przy krawędzi nie robi NIC — dociąganie roku przesunęłoby widok o jedenaście miesięcy w DRUGĄ
     * stronę (styczeń 1910 „miesiąc w tył" to grudzień 1909, a po dociągnięciu roku grudzień 1910).
     */
    const celSkoku = (miesiecy) => {
        const cel = new Date(rok, miesiac + miesiecy, 1);
        if (rokWZakresie(cel.getFullYear())) return cel;
        if (Math.abs(miesiecy) < 12) return null;
        const dociagniety = ograniczRokWidoku(cel);
        const tenSamWidok = dociagniety.getFullYear() === rok && dociagniety.getMonth() === miesiac;
        return tenSamWidok ? null : dociagniety;
    };

    const skok = (miesiecy) => (e) => {
        e.stopPropagation();
        const cel = celSkoku(miesiecy);
        if (cel) onViewDate(cel);
    };

    /** Ocena tego, co stoi w polu: nic (wpis niepełny), komunikat o roku albo komunikat o zakresie pola. */
    const ocen = (t) => {
        const iso = parsujDateZKlawiatury(t);
        if (!iso) { setPowod(null); return null; }
        if (!rokWZakresie(rokZISO(iso))) { setPowod(KOMUNIKAT_ROK); return null; }
        // onWpisanaData oddaje `false`, gdy data jest poza min/max pola — wtedy kalendarz pokazuje ten
        // miesiąc, ale wartość się NIE zmienia; klik w wyłączony dzień też by jej nie zmienił.
        if (onWpisanaData(iso) === false) { setPowod(KOMUNIKAT_ZAKRES); return null; }
        setPowod(null);
        return iso;
    };

    /** Jedyna droga zmiany treści pola — wszystko (klawisz, wklejenie, backspace) idzie przez maskę. */
    const ustawTekst = (nowyTekst, kursor) => {
        kursorDoUstawienia.current = Number.isInteger(kursor) ? kursor : null;
        setWpisPola((p) => ({ tekst: nowyTekst, rew: p.rew + 1 }));
        ocen(nowyTekst);
    };

    const wpis = (e) => {
        const wynik = przeliczMaske(e.target.value, e.target.selectionStart);
        ustawTekst(wynik.tekst, wynik.kursor);
    };

    const wklejenie = (e) => {
        const dane = e.clipboardData && typeof e.clipboardData.getData === "function"
            ? e.clipboardData.getData("text")
            : "";
        if (!dane) return;
        e.preventDefault();
        // Wklejona PEŁNA data w znanym zapisie ląduje jako dd.mm.rrrr, a nie jako sam ciąg cyfr:
        // „1.3.1985" ma być 01.03.1985, nie 13.19.85.
        const iso = parsujDateZKlawiatury(dane);
        if (iso) { ustawTekst(formatujDdMmRrrr(iso), 10); return; }
        const pole = e.target;
        const start = Number.isInteger(pole.selectionStart) ? pole.selectionStart : tekst.length;
        const koniec = Number.isInteger(pole.selectionEnd) ? pole.selectionEnd : start;
        const wynik = przeliczMaske(tekst.slice(0, start) + dane + tekst.slice(koniec), start + dane.length);
        ustawTekst(wynik.tekst, wynik.kursor);
    };

    const klawisz = (e) => {
        if (e.key === "Backspace") {
            const pole = e.target;
            if (pole.selectionStart !== pole.selectionEnd) return; // zaznaczenie kasuje się normalnie
            const wynik = kasujWTyl(tekst, pole.selectionStart);
            if (!wynik) return;
            e.preventDefault();
            ustawTekst(wynik.tekst, wynik.kursor);
            return;
        }
        if (e.key !== "Enter") return;
        e.preventDefault();
        if (ocen(tekst) && onZatwierdz) onZatwierdz();
    };

    return (
        <>
            <div className={styles.header}>
                <button
                    className={styles.navBtn}
                    type="button"
                    onClick={skok(-120)}
                    disabled={celSkoku(-120) === null}
                    aria-label="10 lat wstecz"
                    title="10 lat wstecz"
                >
                    <FiChevronsLeft />
                </button>
                <button
                    className={styles.navBtn}
                    type="button"
                    onClick={skok(-1)}
                    disabled={celSkoku(-1) === null}
                    aria-label="Poprzedni miesiąc"
                    title="Poprzedni miesiąc"
                >
                    <FiChevronLeft />
                </button>
                <select
                    className={styles.wyborMiesiaca}
                    aria-label="Miesiąc"
                    value={miesiac}
                    onChange={(e) => onViewDate(new Date(rok, Number(e.target.value), 1))}
                >
                    {MONTHS_PL.map((nazwa, i) => <option key={nazwa} value={i}>{nazwa}</option>)}
                </select>
                <select
                    className={styles.wyborRoku}
                    aria-label="Rok"
                    value={rok}
                    onChange={(e) => onViewDate(new Date(Number(e.target.value), miesiac, 1))}
                >
                    {zakresLat(rok).map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <button
                    className={styles.navBtn}
                    type="button"
                    onClick={skok(1)}
                    disabled={celSkoku(1) === null}
                    aria-label="Następny miesiąc"
                    title="Następny miesiąc"
                >
                    <FiChevronRight />
                </button>
                <button
                    className={styles.navBtn}
                    type="button"
                    onClick={skok(120)}
                    disabled={celSkoku(120) === null}
                    aria-label="10 lat do przodu"
                    title="10 lat do przodu"
                >
                    <FiChevronsRight />
                </button>
            </div>
            <label className={styles.wpisDatyRow}>
                <span className={styles.wpisDatyLabel}>Wpisz datę</span>
                <input
                    ref={poleRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className={styles.wpisDaty}
                    placeholder="dd.mm.rrrr"
                    maxLength={10}
                    value={tekst}
                    onChange={wpis}
                    onPaste={wklejenie}
                    onKeyDown={klawisz}
                    aria-invalid={powod ? "true" : undefined}
                    title={`Zapis DD.MM.RRRR, lata ${ROK_DOLNY}–${ROK_GORNY}`}
                />
            </label>
            {powod && (
                <div className={styles.wpisDatyBlad} role="alert">{powod}</div>
            )}
        </>
    );
}
